/// <amd-module name="Analytics/Management/AnalyticsDialogComponent" />
import * as Resources from "Analytics/Management/Resources";
import React = require("react");
import * as StringUtils from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IButtonProps } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";
import { getSafeId } from "VSSUI/Util";

export interface IAnalyticsDialogProps {
    dialogTitleText: string;
    dialogBodyText: string;
    featuresListDescription: string;
    confirmButtonText: string;
    learnMoreLinkText: string;
    isSubmitButtonPrimary: boolean;
    isSubmitButtonDanger: boolean;

    onCancel: () => void;
    onSubmit: () => void;
}

interface IAnalyticsDialogState {
    userInputCollectionName: string;
    userInputMatchesCollectionName: boolean;
}

export class AnalyticsDialogComponent extends VssComponent<IAnalyticsDialogProps, IAnalyticsDialogState> {
    private collectionName: string;

    constructor(props: IAnalyticsDialogProps, context: IVssComponentContext) {
        super(props);

        this.collectionName = context.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostName;
    }

    public async componentDidMount() {
        const state: IAnalyticsDialogState = {
            userInputCollectionName: "",
            userInputMatchesCollectionName: false
        };

        this.setState(state);
    }

    public render(): JSX.Element | null {
        if (!this.state) {
            return null;
        }

        const cancelButtonProps: IButtonProps = {
            text: Resources.CancelButtonText,
            onClick: this.props.onCancel
        };

        const confirmButtonProps: IButtonProps = {
            text: this.props.confirmButtonText,
            primary: this.props.isSubmitButtonPrimary,
            danger: this.props.isSubmitButtonDanger,
            disabled: this.props.isSubmitButtonDanger && !this.state.userInputMatchesCollectionName,
            onClick: this.props.onSubmit
        };

        return (
            <Dialog
                titleProps={{ text: this.props.dialogTitleText }}
                footerButtonProps={[cancelButtonProps, confirmButtonProps]}
                onDismiss={this.props.onCancel}
            >
                {this.renderDialogBody()}
            </Dialog>
        );
    }

    private renderDialogBody(): JSX.Element {
        return (
            <div>
                {this.props.dialogBodyText}

                <br />
                <br />

                {this.props.featuresListDescription}
                <ul className="analytics-management-feature-list">
                    <li>{Resources.AnalyticsExperienceListWidgetsItem}</li>
                    <li>{Resources.AnalyticsExperienceListInProductItem}</li>
                    <li>{Resources.AnalyticsExperienceListPowerBiItem}</li>
                    <li>{Resources.AnalyticsExperienceListODataItem}</li>
                </ul>

                <Link href={Resources.StateExplanationLinkUrl} target="_blank">
                    {this.props.learnMoreLinkText}
                </Link>

                {this.props.isSubmitButtonDanger ? this.renderValidationTextBox() : null}
            </div>
        );
    }

    private renderValidationTextBox(): JSX.Element {
        const confirmationFieldId = `confirmation-${StringUtils.newGuid()}`;
        return (
            <div className="analytics-management-confirmation">
                <label htmlFor={getSafeId(confirmationFieldId)}>
                    <FormatComponent className="analytics-management-confirmation-instructions" format={Resources.CollectionValidationInstructions}>
                        <b>{this.collectionName}</b>
                    </FormatComponent>
                </label>

                <TextField
                    inputId={confirmationFieldId}
                    className="analytics-management-confirmation-text"
                    value={this.state.userInputCollectionName}
                    onChange={(e, newInputValue) => this.checkIfInputMatchesCollection(newInputValue)}
                />
            </div>
        );
    }

    private checkIfInputMatchesCollection = (newInputValue: string) => {
        const state: IAnalyticsDialogState = {
            userInputCollectionName: newInputValue,
            userInputMatchesCollectionName: StringUtils.localeIgnoreCaseComparer(this.collectionName, newInputValue) === 0 ? true : false
        };

        this.setState(state);
    };
}
